import { DBO_D } from '@op/utils/format';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * 根据配置处理字符串数组
 * @param arr 原始数组
 * @param normalizeToLowercase 是否转换为小写，默认 true
 */
function normalizeStrings(
  arr: string[] | undefined,
  normalizeToLowercase = true
): string[] {
  if (!arr) return [];
  if (!normalizeToLowercase) return arr;
  return arr.map(item =>
    typeof item === 'string' ? item.toLocaleLowerCase() : item
  );
}

export function getLevelOneInfo(options: any[], markets: string[]) {
  const oneLevel: Array<string> = [];
  // 查找一级分类，因为选中部分二级分类并不会返回该一级分类
  options.forEach((item: any) => {
    const flag = item.children.some((v: any) => markets.includes(v.value));
    if (flag) oneLevel.push(item.value);
  });

  return oneLevel;
}

// 处理二级选择器的过滤器
export default function useDefaultTwoLevelFilter({
  originalOptions,
  defaultValue = {},
  oriDefaultFilter = {},
  dealwithDefaultValue,
  onlySecondLevelValue = false,
  dealwithAllValue,
  filterValue,
  beginWatch = false,
  watchOptionsSetDefaultValue = false,
  firstExcludeOptions = [],
  backOriDefaultValue,
  normalizeToLowercase = true,
}: {
  firstExcludeOptions?: Array<string>;
  originalOptions: Array<{ children: any[]; value: any }>;
  defaultValue?: any;
  oriDefaultFilter?: any;
  backOriDefaultValue?: (value: {
    value: string[];
    oneLevel: any[];
    type: any;
    isGLOBAL_WO_CHINA: boolean;
  }) => void;
  filterValue?: any;
  onlySecondLevelValue?: boolean;
  beginWatch?: boolean;
  watchOptionsSetDefaultValue?: boolean;
  normalizeToLowercase?: boolean;
  dealwithDefaultValue: (
    defaultValue: any,
    isFirst: boolean,
    originalOptions: any,
  ) => {
    value: Array<string>;
    type: string;
    auxiliaryInfo?: any;
    marketsValue?: 'global' | 'global_wo_china';
  };
  dealwithAllValue?: (
    originalOptions: any,
    auxiliaryInfo: any,
  ) => {
    markets: Array<string>;
    regions: Array<string>;
    type: string;
  };
}) {
  const oriDefaultValueRef = useRef<any>({
    markets: ['global'],
    regions: [],
    type: 'market',
  });
  const [selectValue, setSelectValue] = useState<any>({
    markets: ['global'],
    regions: [],
    type: 'market',
  });

  const dealwithFilterValue = (
    data: any,
    localFirstExcludeOptions: any = [],
    isFirstValue = false,
  ) => {
    if (originalOptions.length > 0) {
      const {
        value: defaultSelected,
        type,
        marketsValue = 'global',
        auxiliaryInfo,
      } = dealwithDefaultValue(data || {}, isFirstValue, originalOptions);

      // 根据配置决定是否转换为小写
      const normalizedDefaultSelected = normalizeStrings(defaultSelected, normalizeToLowercase);



      let selectValue: any = {
        markets: [],
        regions: [],
        type: type || 'market',
      };

      // 非全选，需要查找值
      if (defaultSelected.length === 1 && defaultSelected[0] === '-99999') {
        selectValue = { markets: [], regions: [], type: 'market' };
      } else if (normalizedDefaultSelected?.length !== 0) {
        if (type === 'market') {
          selectValue.markets = normalizedDefaultSelected || [];
          const lengthMap: { [key: string]: number } = {};
          // 查找对应的一级数据
          originalOptions.forEach((item: { children: any[]; value: any }) => {
            const hasRegion = item.children.every(
              ({ value: itemValue }: { value: string }) =>
                normalizedDefaultSelected?.includes?.(itemValue),
            );
            lengthMap[item.value] = item.children?.length || 0;
            hasRegion && selectValue.regions.push(item.value);
          });
          // 判断是否是region
          let total = 0;
          selectValue.regions.forEach((key: string) => {
            total += lengthMap[key];
          });
          if (total === normalizedDefaultSelected.length) {
            selectValue.type = 'region';
          }
        } else {
          selectValue.regions = normalizedDefaultSelected;
          // 查找对应的二级数据
          originalOptions.forEach(
            (item: { value: any; children: { value: string }[] }) => {
              if (normalizedDefaultSelected.includes(item.value)) {
                selectValue.markets.push(
                  ...item.children.map(({ value }: { value: string }) => value),
                );
              }
            },
          );
        }
      } else {
        if (dealwithAllValue) {
          selectValue = dealwithAllValue(originalOptions, auxiliaryInfo);
          if (localFirstExcludeOptions?.length > 0) {
            const preSelectValue = selectValue.markets.length;
            selectValue.markets = [...selectValue.markets].filter(
              item => !localFirstExcludeOptions.includes(item),
            );
            if (preSelectValue !== selectValue.markets.length) {
              selectValue.regions = selectValue.regions.filter(
                (item: string) => item !== 'Account',
              );
            }
          }
        } else {
          selectValue.type = 'market';
          selectValue.markets = [marketsValue];
        }
      }
      isFirst.current = false;
      return selectValue;
    }
  };

  const onChange = (tags: any, backAllLevelOne = false) => {
    const { regions, markets } = tags;
    let { type } = tags;
    let propsRegions: Array<string> = [];
    let oneLevel: Array<any> = [];
    let isGLOBAL_WO_CHINA = false;

    if (type === 'market') {
      propsRegions = markets;
      if (markets.length === 1) {
        // 下面两个情况需要将type转为 "region", 这个是region需要的判断
        const value = markets[0];
        if (value === 'global_wo_china') {
          propsRegions = originalOptions
            .map((item: any) => item.value)
            .filter((item: any) => item !== 'chn');
          isGLOBAL_WO_CHINA = true;
          type = 'region';
        } else if (value === 'global') {
          propsRegions = [];
          type = 'region';
        }
      }
    } else {
      if (onlySecondLevelValue) {
        originalOptions.forEach(
          (item: { value: any; children: { value: string }[] }) => {
            if (regions.includes(item.value)) {
              propsRegions.push(
                ...item.children.map(({ value }: { value: string }) => value),
              );
            }
          },
        );
      } else {
        propsRegions = regions;
      }
    }
    if (backAllLevelOne) {
      oneLevel = getLevelOneInfo(originalOptions, markets);
    }
    return { value: propsRegions, oneLevel, type, isGLOBAL_WO_CHINA };
  };

  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      const localDefaultValue = dealwithFilterValue(
        defaultValue,
        firstExcludeOptions,
        isFirst.current,
      );
      if (localDefaultValue) {
        setSelectValue(localDefaultValue);
      }

      const oriDefaultValue = dealwithFilterValue(
        oriDefaultFilter,
        firstExcludeOptions,
        true,
      );

      if (oriDefaultValue) {
        oriDefaultValueRef.current = oriDefaultValue;
        backOriDefaultValue?.(oriDefaultValue);
      }
    }
  }, [originalOptions]);

  useEffect(() => {
    if ((!isFirst.current && filterValue?.start_time) || beginWatch) {
      const localFilterValue = dealwithFilterValue(
        filterValue,
        firstExcludeOptions,
      );

      if (localFilterValue) {
        setSelectValue(localFilterValue);
      }
    }
  }, [filterValue, originalOptions]);

  useEffect(() => {
    if (!isFirst.current && watchOptionsSetDefaultValue) {
      const localFilterValue = dealwithFilterValue(defaultValue);

      if (localFilterValue) {
        setSelectValue(localFilterValue);
      }
    }
  }, [watchOptionsSetDefaultValue, originalOptions]);

  const diffClassName = useMemo(() => {
    const { markets, regions } = selectValue;
    const { markets: oldMarkets, regions: oldRegions } =
      oriDefaultValueRef.current;

    const flag = !DBO_D.compareArray(markets, oldMarkets);
    if (onlySecondLevelValue) {
      if (flag) return 'dbo-diff-filter';
    } else {
      if (flag || !DBO_D.compareArray(regions, oldRegions))
        return 'dbo-diff-filter';
    }
  }, [selectValue]);

  return {
    selectValue,
    onChange,
    setSelectValue,
    diffClassName,
    isFirst
  };
}
